import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  Paper,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Link,
} from '@mui/material';
import { SmallBusinessPlan, SmallBusinessPlanService } from 'us-web-services';
import PageStyles from 'src/styles/PageStyles';
import { axiosErrorMessage } from './axiosErrorMessage';

type Tier = 'STARTER' | 'GROWTH' | 'PRO';
type Interval = 'MONTHLY' | 'ANNUAL';

interface TierConfig {
  tier: Tier;
  label: string;
  seatLimit: number;
}

const TIERS: TierConfig[] = [
  { tier: 'STARTER', label: 'Starter', seatLimit: 10 },
  { tier: 'GROWTH', label: 'Growth', seatLimit: 25 },
  { tier: 'PRO', label: 'Pro', seatLimit: 50 },
];

const INTERVALS: { interval: Interval; label: string }[] = [
  { interval: 'MONTHLY', label: 'Monthly' },
  { interval: 'ANNUAL', label: 'Annual' },
];

function cellKey(tier: Tier, interval: Interval): string {
  return `${tier}-${interval}`;
}

function planName(label: string, interval: Interval): string {
  return `${label} ${interval === 'MONTHLY' ? 'Monthly' : 'Annual'}`;
}

function dollarsFromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

function SmallBusinessPricingPlans({ history }: RouteComponentProps) {
  const { classes } = PageStyles();
  const [plans, setPlans] = useState<Record<string, SmallBusinessPlan>>({});
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await SmallBusinessPlanService.findByFilter({ active: true, limit: 100 });
      const active = response?.data?.data ?? [];
      const byCell: Record<string, SmallBusinessPlan> = {};
      const nextPrices: Record<string, string> = {};

      active.forEach((plan) => {
        byCell[`${plan.tier}-${plan.billingInterval}`] = plan;
      });
      TIERS.forEach(({ tier }) =>
        INTERVALS.forEach(({ interval }) => {
          const existing = byCell[cellKey(tier, interval)];

          nextPrices[cellKey(tier, interval)] = existing ? dollarsFromCents(existing.priceCents) : '';
        })
      );
      setPlans(byCell);
      setPrices(nextPrices);
    } catch (e: unknown) {
      setError(axiosErrorMessage(e, 'Failed to load pricing plans'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setPrice = (key: string, value: string) =>
    setPrices((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setError(null);
    setNotice(null);

    for (const { tier, label } of TIERS) {
      for (const { interval } of INTERVALS) {
        const raw = prices[cellKey(tier, interval)]?.trim();

        if (!raw) continue;
        const cents = Math.round(parseFloat(raw) * 100);

        if (!Number.isFinite(cents) || cents < 1) {
          setError(`Enter a valid price for ${label} ${interval} (e.g. 129.00)`);

          return;
        }
      }
    }

    setSaving(true);
    let created = 0;
    let repriced = 0;

    try {
      for (const { tier, label, seatLimit } of TIERS) {
        for (const { interval } of INTERVALS) {
          const key = cellKey(tier, interval);
          const raw = prices[key]?.trim();

          if (!raw) continue;
          const cents = Math.round(parseFloat(raw) * 100);
          const existing = plans[key];

          if (!existing) {
            await SmallBusinessPlanService.create({
              tier,
              billingInterval: interval,
              name: planName(label, interval),
              description: null,
              priceCents: cents,
              seatLimit,
            });
            created += 1;
          } else if (cents !== existing.priceCents) {
            await SmallBusinessPlanService.reprice(existing.id, cents);
            repriced += 1;
          }
        }
      }

      const parts = [];

      if (created) parts.push(`${created} created`);
      if (repriced) parts.push(`${repriced} repriced`);
      setNotice(parts.length ? `Saved — ${parts.join(', ')}.` : 'No changes to save.');
      await load();
    } catch (e: unknown) {
      setError(axiosErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const dirty = useMemo(
    () =>
      TIERS.some(({ tier }) =>
        INTERVALS.some(({ interval }) => {
          const key = cellKey(tier, interval);
          const raw = prices[key]?.trim();
          const existing = plans[key];

          if (!raw) return false;
          const cents = Math.round(parseFloat(raw) * 100);

          return !existing || cents !== existing.priceCents;
        })
      ),
    [prices, plans]
  );

  return (
    <Paper className={classes.paper}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
        <Typography variant='h5'>Small Business Pricing Plans</Typography>
        <Button variant='contained' onClick={save} disabled={saving || loading || !dirty}>
          {saving ? 'Saving…' : 'Save prices'}
        </Button>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Tiers and seat limits are fixed. Set the monthly and annual price for each tier and save —
        empty tiers are created, price changes reprice (a new Stripe Price is created and the old one archived;
        existing subscriptions keep their captured price until renewal).
      </Typography>

      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity='success' sx={{ mb: 2 }}>{notice}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Table size='small' sx={{ maxWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>Tier</TableCell>
              {INTERVALS.map(({ interval, label }) => (
                <TableCell key={interval}>{label} price</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {TIERS.map(({ tier, label, seatLimit }) => (
              <TableRow key={tier}>
                <TableCell>
                  <Typography variant='subtitle2'>{label}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {seatLimit} seats
                  </Typography>
                </TableCell>
                {INTERVALS.map(({ interval }) => {
                  const key = cellKey(tier, interval);
                  const existing = plans[key];

                  return (
                    <TableCell key={interval}>
                      <TextField
                        size='small'
                        value={prices[key] ?? ''}
                        onChange={(e) => setPrice(key, e.target.value)}
                        placeholder='0.00'
                        InputProps={{
                          startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                        }}
                        sx={{ width: 140 }}
                      />
                      <Box sx={{ mt: 0.5 }}>
                        {existing ? (
                          <Link
                            component='button'
                            type='button'
                            variant='caption'
                            onClick={() => history.push(`/pricing-plans/${existing.id}`)}
                          >
                            Manage / archive
                          </Link>
                        ) : (
                          <Typography variant='caption' color='text.secondary'>
                            Not created
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}

export default withRouter(SmallBusinessPricingPlans);
